import { ValueOf } from 'type-fest';

type ValueType = 'number' | 'string' | 'currency';

type VariableGroupDisplayContext = {
  name: string;
  position: number;
  defaultCollapsed: boolean;
  variables: string[];
};

type VariableDisplayContext = {
  primaryKey: string;
  secondaryKey: string;
  value: number | string | null;
  valueType: ValueType;
  position: number;
  variableType: 'standard';
  quantitySpecific: boolean;
  qtySpecificDict: Record<string, number | string | null> | null;
  description: string | null;
};

type TableVariableDisplayContext = {
  primaryKey: string;
  secondaryKey: string;
  value: Array<{ key: string; value: unknown }> | null;
  rowNumbers: number[];
  tableName: string;
  displayColumn: string;
  position: number;
  variableType: 'table';
  quantitySpecific: boolean;
  qtySpecificDict: Record<
    string,
    {
      value: Array<{ key: string; value: unknown }> | null;
      rowNumbers: number[];
    }
  > | null;
  description: string | null;
};

type DropDownVariableDisplayContext = {
  primaryKey: string;
  secondaryKey: string;
  value: unknown;
  options: unknown[];
  valueType: ValueType;
  position: number;
  variableType: 'drop_down';
  quantitySpecific: boolean;
  qtySpecificDict: Record<
    string,
    {
      value: unknown;
      options: unknown[];
    }
  > | null;
  description: string | null;
};

export type DisplayContext = {
  groups: VariableGroupDisplayContext[];
  variables: (
    | VariableDisplayContext
    | TableVariableDisplayContext
    | DropDownVariableDisplayContext
  )[];
};

type VendorQuote = {
  created: string;
  requested: string;
  requestStatus: string;
  status: string;
  uuid: string;
  vendor: string;
};

type VendorMaterialPricingPlan = {
  id: number;
  vendorQuotes: VendorQuote[];
};

export type OverrideAudit = {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  action: 'override' | 'clear';
  key: string; // variable name or field it lines up with
  value: string | number | null;
  quantity?: number | null;
  timestamp: string;
};

type TypedVariableValues = number | string;

type TypedVariableOverrides =
  | Record<string, TypedVariableValues>
  | TypedVariableValues;

type TypedTableVariableValues = Record<string, boolean | number | string>;

type TypedTableVariableOverrides =
  | Record<string, TypedTableVariableValues>
  | TypedTableVariableValues;

export type PricingEngineOverrides = {
  variables: TypedVariableOverrides;
  dropDownVariables: TypedVariableOverrides;
  tableVariables: TypedTableVariableOverrides;
};

export type PartialPricingEngineOverrides = Partial<PricingEngineOverrides>;

export const SETUP_TIME_DISPLAY_UNITS = {
  SECONDS: 'sec',
  MINUTES: 'min',
  HOURS: 'hr',
} as const;
export const RUNTIME_DISPLAY_UNITS = {
  ...SETUP_TIME_DISPLAY_UNITS,
  PARTS_PER_HOUR: 'pph',
  PARTS_PER_MINUTE: 'ppm',
} as const;
export type SetupTimeDisplayUnits = ValueOf<typeof SETUP_TIME_DISPLAY_UNITS>;
export type RuntimeDisplayUnits = ValueOf<typeof RUNTIME_DISPLAY_UNITS>;

export type OperationData = {
  id: number;
  name: string;
  isFinish: boolean;
  isOutsideService: boolean;
  componentId: number;
  opDefId: number;
  formulaId: number;
  notes: string | null;
  calculatedNotes: string | null;
  position: number;
  overrides: PricingEngineOverrides | null;
  overridesAudit: OverrideAudit[] | null;
  fieldOverrideAudits: Record<string, OverrideAudit[]> | null;
  category: 'material' | 'operation';
  displayContext: DisplayContext;
  isFromErpImport: boolean;
  isConnectedToVendorRfq: boolean;
  taskSet: string[];
  runtimeDisplayUnits: RuntimeDisplayUnits;
  setupTimeDisplayUnits: SetupTimeDisplayUnits;
  isSheetOptimizable: boolean;
  isTubeOptimizable: boolean;
  vendorMaterialPricingPlan: VendorMaterialPricingPlan | null;
};

export type OperationCellData = {
  id: number;
  quantityId: number;
  operationId: number;
  leadTime: number | null;
  manualLeadTime: number | null;
  price: string;
  manualPrice: string | null;
  unitPrice: string;
  manualUnitPrice: string | null;
  overridesAudit: OverrideAudit[] | null;
};

export type OperationWithCells = OperationData & {
  cells: OperationCellData[];
};
